import { useCallback } from "react";

import type { TllMasterObject } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { SiteControllerSettingBaseFormModelOnSetViewModelType } from "../../../../base/use-case/use-create-model/use-create-model";
import type { SiteControllerSettingTllFormModel } from "../../../type";

type Props = {
	viewModel: SiteControllerSettingTllFormModel["ViewModel"];
	onSetViewModel: SiteControllerSettingBaseFormModelOnSetViewModelType;
};

export type SiteControllerSettingTLLincolnFormModelMethodsType = {
	siteControllerSettingTLLFormModelMethods: {
		onSetTllID: (input: string) => void;
		onSetChillnnRoomID: (value: { tllRoomID: string; chillnnRoomID: string }) => void;
		onSetDefaultPlan: (value: string) => void;
	};
};

type UseSiteControllerSettingTLLincolnFormModelMethodsType = ({
	viewModel,
	onSetViewModel,
}: Props) => SiteControllerSettingTLLincolnFormModelMethodsType;

export const useSiteControllerSettingTLLincolnFormModelMethods: UseSiteControllerSettingTLLincolnFormModelMethodsType =
	({ viewModel, onSetViewModel }) => {
		const { tll: _tll } = viewModel;

		const onSetTllID = useCallback(
			(input: string) => {
				const { tll } = viewModel;
				const { tllID } = tll;

				const newValue: ForAutoGenerator<TllMasterObject, "hotelID"> = {
					...tll,
					tllID: {
						...tllID,
						value: input,
					},
				};

				onSetViewModel([
					{
						key: "tll",
						value: newValue,
					},
				]);
			},
			[onSetViewModel, viewModel],
		);

		const onSetChillnnRoomID = useCallback(
			(value: { tllRoomID: string; chillnnRoomID: string }) => {
				const { tll } = viewModel;
				const { rooms } = tll;
				const newValue: ForAutoGenerator<TllMasterObject, "hotelID"> = {
					...tll,
					rooms: {
						isEdited: true,
						hasError: false,
						value: rooms.value.map((room) => {
							if (room.tllRoomID.value === value.tllRoomID) {
								return {
									...room,
									chillnnRoomID: {
										isEdited: true,
										value: value.chillnnRoomID,
									},
								};
							}

							return room;
						}),
					},
				};

				onSetViewModel([
					{
						key: "tll",
						value: newValue,
					},
				]);
			},
			[onSetViewModel, viewModel],
		);

		const onSetDefaultPlan = useCallback(
			(value: string) => {
				const { tll } = viewModel;

				const newValue: ForAutoGenerator<TllMasterObject, "hotelID"> = {
					...tll,
					defaultPlan: {
						isEdited: true,
						value,
					},
				};

				onSetViewModel([
					{
						key: "tll",
						value: newValue,
					},
				]);
			},
			[onSetViewModel, viewModel],
		);

		return {
			siteControllerSettingTLLFormModelMethods: {
				onSetTllID,
				onSetChillnnRoomID,
				onSetDefaultPlan,
			},
		};
	};
